import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
} from "react";

import { getUploadUrl } from "@repo/edtech-api";
import { Checkbox, ImageModalViewer } from "@repo/edtech-ui-kit";
import clsx from "clsx";

import {
  QuestionComponentProps,
  useTestSolutionActionsContext,
  useTestSolutionValuesContext,
} from "../../../../../../../../model";
import { useTestInputVisibility } from '../../../../../../../../model/providers/test-solution-provider/useTestVisibility';
import { getConfig } from "../../../../../../../../origins";
import { CommentBlock } from '../comment-block/CommentBlock';

import st from "./ClosedMany.module.scss";

type Props = QuestionComponentProps;

export const ClosedMany: FC<Props> = ({
  answers,
  userAnswer,
  setUserAnswer,
  currentQuestion,
}) => {
  const { isImageWithModal } = getConfig();

  const userAnswerT = Array.isArray(userAnswer)? userAnswer as unknown as number[]: [];
  const setUserAnswerT = setUserAnswer as Dispatch<SetStateAction<number[]>>;

  const { setIsCommentRequiredAndEmpty, setUserComment } = useTestSolutionActionsContext();
  const { userComment } = useTestSolutionValuesContext();

  const isVisibleInput = useTestInputVisibility({
    answerInput: userAnswerT,
    currentQuestion,
  });

  const onChange = (index: number) => {
    setUserAnswerT((prev) => {
      if (!prev) {
        return [ index ];
      }
      if (Array.isArray(prev) && prev.includes(index)) {
        return prev.filter((i) => i !== index);
      }
      if (Array.isArray(prev) && !prev.includes(index)) {
        return [ ...prev, index ];
      }
      return prev;
    });
  };

  useEffect(() => {
    const isCommentRequired = currentQuestion?.necessaryUserComment === 'checked';

    if (!isVisibleInput || !isCommentRequired) {
      setIsCommentRequiredAndEmpty(false);
      return;
    }

    setIsCommentRequiredAndEmpty(userComment.trim() === '');
  }, [
    isVisibleInput,
    userComment,
    setIsCommentRequiredAndEmpty,
    currentQuestion?.necessaryUserComment,
  ]);

  return (
    <div className={st.closed}>
      {
        currentQuestion?.questionImage && (
          <ImageModalViewer
            alt="closed-img"
            className={st.image}
            disabledModal={!isImageWithModal}
            src={getUploadUrl(currentQuestion.questionImage)}
          />
        )
      }
      <ul>
        {
          answers?.map((answer, index) => (
            <li
              key={index}
              className={st.li}
            >
              <Checkbox
                checked={userAnswerT.includes(index)}
                classNames={
                  clsx(st.answer,
                    userAnswerT.includes(index) && st.checked)
                }
                iconClassNames={st.checked_icon}
                label={
                  (<div
                    className={
                      clsx(st.text,
                        userAnswerT.includes(index) && st.checked)
                    }
                  >
                    {answer}
                  </div>)
                }
                onChange={() => onChange(index)}
              />
            </li>
          ))
        }
      </ul>
      {
        isVisibleInput && (
          <CommentBlock
            comment={userComment}
            setComment={(event) => setUserComment(event.target.value)}
            title={currentQuestion?.userCommentPlaceholder}
          />
        )
      }
    </div>
  );
};

